import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Button,
  Spin,
  Alert,
  message,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dynamicFormsService from '../../services/dynamicFormsService';
import FormPageView from '../../components/dynamicForms/FormPageView';
import { formStrings } from '../../components/dynamicForms/formStrings';

const DynamicForm = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(false);

  useEffect(() => {
    loadForm();
  }, [slug]);

  const loadForm = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dynamicFormsService.getFormBySlug(slug);

      // Check if form is inactive (checking multiple possible locations)
      const isActive = data?.form?.is_active ?? data?.is_active ?? 1;
      // Handle both boolean and number values
      if (isActive === 0 || isActive === false || isActive === '0') {
        // Redirect to expired page instead of showing disabled form
        navigate('/form-expired', { replace: true });
        return;
      }

      setFormData(data);
      setIsFormDisabled(false);
    } catch (err) {
      setError('Form not found or unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const t = formStrings(formData?.form?.language_id);
    const { fields } = formData;
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);

      // Replace __other__ values with custom text from form values
      const processedValues = { ...values };
      Object.keys(processedValues).forEach(fieldName => {
        const value = processedValues[fieldName];
        const otherTextField = `${fieldName}_other_text`;

        // Handle select and radio (string values)
        if (value === '__other__' && processedValues[otherTextField]) {
          processedValues[fieldName] = processedValues[otherTextField];
          // Remove the _other_text field
          delete processedValues[otherTextField];
        }

        // Handle checkbox (array values)
        if (Array.isArray(value) && value.includes('__other__') && processedValues[otherTextField]) {
          const index = value.indexOf('__other__');
          processedValues[fieldName] = [
            ...value.slice(0, index),
            processedValues[otherTextField],
            ...value.slice(index + 1)
          ];
          // Remove the _other_text field
          delete processedValues[otherTextField];
        }
      });

      // Check if form has file fields
      const hasFiles = fields.some(field => field.field_type === 'file');

      let response;

      if (hasFiles) {
        // Use FormData for forms with files
        const submitData = new FormData();

        fields.forEach(field => {
          const value = processedValues[field.field_name];

          if (field.field_type === 'file') {
            // Handle file upload
            if (value && value.length > 0) {
              const file = value[0].originFileObj || value[0];
              submitData.append(field.field_name, file);
            }
          } else if (value !== undefined && value !== null) {
            // Handle other field types
            if (field.field_type === 'date' && value) {
              submitData.append(field.field_name, value.format('YYYY-MM-DD'));
            } else if (field.field_type === 'boolean') {
              submitData.append(field.field_name, value ? '1' : '0');
            } else if (field.field_type === 'checkbox' && Array.isArray(value)) {
              // Checkbox arrays should be sent as JSON string in FormData
              submitData.append(field.field_name, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              // Any other arrays
              submitData.append(field.field_name, JSON.stringify(value));
            } else {
              submitData.append(field.field_name, value.toString());
            }
          }
        });

        response = await dynamicFormsService.submitFormMultipart(formData.form.form_id, submitData);
      } else {
        // Use JSON for forms without files
        const submissionData = {};
        fields.forEach(field => {
          const value = processedValues[field.field_name];

          if (value !== undefined && value !== null) {
            if (field.field_type === 'date' && value) {
              submissionData[field.field_name] = value.format('YYYY-MM-DD');
            } else if (field.field_type === 'boolean') {
              submissionData[field.field_name] = value ? 1 : 0;
            } else {
              submissionData[field.field_name] = value;
            }
          }
        });

        response = await dynamicFormsService.submitForm(formData.form.form_id, submissionData);
      }

      if (response.success) {
        // Check if payment is enabled and form has a payment link
        const paymentEnabledRaw = formData.form?.payment_enabled ?? formData?.payment_enabled;
        const paymentEnabled = paymentEnabledRaw === 1 || paymentEnabledRaw === true || paymentEnabledRaw === '1';
        const paymentLink = formData.form?.payment_link ?? formData?.payment_link;

        if (paymentEnabled && paymentLink) {
          // Redirect to payment link
          message.success(t.redirectingToPayment, 2);
          setTimeout(() => {
            window.location.href = paymentLink;
          }, 1500);
        } else {
          // Show standard success message
          setSuccess(true);
          form.resetFields();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Check if it's a workshop capacity error
        if (response.workshop_full) {
          const errorMessage = response.error || t.workshopsFull;
          message.error(errorMessage, 5);
          setError(errorMessage);
          // Optionally refresh the form data to get updated capacity
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        } else {
          throw new Error(response.error || t.submitFailed);
        }
      }
    } catch (err) {
      setError(err.message || t.submitFailed);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto' }}>
        <Alert
          message="Form Not Found"
          description={error || 'The requested form could not be found'}
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate('/client-portal')}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <FormPageView
      data={formData}
      form={form}
      onFinish={handleSubmit}
      submitting={submitting}
      success={success}
      error={error}
      onCloseSuccess={() => setSuccess(false)}
      onCloseError={() => setError(null)}
      isFormDisabled={isFormDisabled}
      onLogoClick={() => navigate('/')}
    />
  );
};

export default DynamicForm;
